import { Icon } from "../ui/Icon";
import { STATUS_META, PRIORITY_META, type TaskStatus } from "./taskMeta";

/**
 * PROJE ÜSTÜ görev tablosu — sorgu YAPMAZ, yalnız sunar.
 *
 * İki yerde kullanılır: "Bana atananlar" sekmesi ve KPI drill-down drawer'ı.
 * Tek bileşen olması şart: iki kopya olsaydı sütunlar ve rozetler zamanla
 * ayrışır, aynı görev iki ekranda farklı görünürdü.
 */

export interface PortfolioRow {
  id: string;
  code: string;
  title: string;
  wbs: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  status: string;
  priority: string;
  progressPct: number;
  plannedEnd: string | Date | null;
  isMilestone: boolean;
  childCount: number;
  assignees: Array<{ userId: string; role: string; name: string }>;
}

interface Props {
  rows: PortfolioRow[];
  total: number;
  truncated: boolean;
  loading: boolean;
  error: string | null;
  emptyTitle: string;
  emptyHint: string;
  /** Çağıran ekranda arama kutusu var mı — kırpma uyarısı buna göre yönlendirir. */
  searchable?: boolean;
  onOpenTask: (id: string) => void;
  /** Proje künyesine tıklayınca o projeye geç. Drawer içinde verilmez. */
  onOpenProject?: (id: string) => void;
}

function fmtDate(d: string | Date | null): string {
  return d ? new Date(d).toLocaleDateString("tr-TR") : "—";
}

/** Bitişi bugünden önce olan AÇIK görev — satırda ayrıca metinle işaretlenir. */
function isOverdue(r: PortfolioRow): boolean {
  if (!r.plannedEnd || r.status === "done" || r.status === "cancelled") return false;
  const end = new Date(r.plannedEnd);
  const now = new Date();
  return end < new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function PortfolioTaskList({
  rows, total, truncated, loading, error, emptyTitle, emptyHint, searchable, onOpenTask, onOpenProject,
}: Props) {
  if (error) return <div className="form-banner-err">{error}</div>;
  if (loading) return <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>;
  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <span className="ic" aria-hidden="true"><Icon name="check" size={28} /></span>
        <p className="ttl">{emptyTitle}</p>
        <p className="sub">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div className="dt2-scroll">
      <table className="dt2">
        <thead>
          <tr>
            <th className="ptl-proj">Proje</th>
            <th>Görev</th>
            <th className="ptl-st">Durum</th>
            <th className="ptl-pri">Öncelik</th>
            <th className="ptl-people">Sorumlular</th>
            <th className="num ptl-prog">İlerleme</th>
            <th className="ptl-end">Bitiş</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const m = STATUS_META.task[r.status as TaskStatus];
            const pm = PRIORITY_META[r.priority as keyof typeof PRIORITY_META];
            const late = isOverdue(r);
            return (
              // Satır tıklanabilir: klavye kullanıcısı da açabilmeli (CLAUDE.md ✅6/✅8).
              // <tr> semantik buton olamadığı için rol+tabIndex+Enter/Space veriliyor.
              <tr key={r.id} className="ptl-row" title="Detayı aç"
                role="button" tabIndex={0}
                aria-label={`${r.title} — detayı aç`}
                onClick={() => onOpenTask(r.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenTask(r.id); }
                }}>
                <td>
                  {onOpenProject ? (
                    // Satır tıklaması görevi açıyor — projeye geçiş yayılımı KESMELİ.
                    <button type="button" className="ptl-projbtn" title={`${r.projectName} projesine git`}
                      onClick={(e) => { e.stopPropagation(); onOpenProject(r.projectId); }}>
                      <span className="mono">{r.projectCode}</span>
                      <span className="ptl-projname">{r.projectName}</span>
                    </button>
                  ) : (
                    <span className="ptl-projstatic">
                      <span className="mono">{r.projectCode}</span>
                      <span className="ptl-projname">{r.projectName}</span>
                    </span>
                  )}
                </td>
                <td>
                  <span className="ptl-title">
                    {r.isMilestone ? <Icon name="star" size={12} /> : null}
                    <span className="mono cell-id">{r.wbs}</span>
                    {r.title}
                    {/* Özet görev: KPI'lar yaprak sayar, bu satır sayıya dahil DEĞİL.
                        Rozetlemezsek kullanıcı listeyi KPI ile karşılaştırıp şaşırır. */}
                    {r.childCount > 0 ? (
                      <span className="badge-pill b-info" title="Alt görevleri var — ilerleme onlardan hesaplanır">
                        <Icon name="list" size={11} />özet
                      </span>
                    ) : null}
                  </span>
                </td>
                <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                <td><span className={`badge-pill ${pm.cls}`}><Icon name={pm.icon} size={11} />{pm.label}</span></td>
                <td className="prj-cell-people">{r.assignees.length ? r.assignees.map((a) => a.name).join(", ") : "—"}</td>
                <td className="cell-num mono">%{r.progressPct}</td>
                <td className="mono">
                  {/* Gecikme RENK TEK BAŞINA anlatılamaz (CLAUDE.md kural 5):
                      ikon + "geçti" metni tarihle birlikte. */}
                  {late ? <span className="ptl-late"><Icon name="alertTriangle" size={11} />{fmtDate(r.plannedEnd)} geçti</span> : fmtDate(r.plannedEnd)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="dt2-foot">
        <span>{total} görev</span>
        {truncated ? (
          <span className="ptl-trunc">
            <Icon name="alertTriangle" size={11} />
            {/* Drawer'da arama kutusu YOK; olmayan bir kontrole yönlendirmek
                kullanıcıyı çaresiz bırakır. Metin çağrı yerine göre değişir. */}
            İlk {rows.length} satır gösteriliyor ({total} görevden)
            {searchable ? " — aramayla daraltın." : " — tamamı için Liste (WBS) sekmesini kullanın."}
          </span>
        ) : null}
      </div>
    </div>
  );
}
